import { Injectable } from '@angular/core';
import { isEqual, cloneDeep } from 'lodash';
import { SharedConstants } from '@formbird/types';
import { DocumentUpdater, UtilString } from '@formbird/shared';
import { DocumentService } from './document.service';
import { ModifiedFieldService } from './modified-field.service';
import { ValidationService } from '../validation/validation.service';
import { UnsavedDocumentService } from './unsaved-document.service';
import { PreProcessorFieldService } from '../preprocessor/pre-processor-field.service';
import { BroadcastService } from '../broadcast/broadcast.service';
import { IApplicationState } from '../../redux/state/application.state';
import { formSetDocument } from '../../redux/actions';
import { AppStore } from '../../redux/store/app.store';

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class DocumentUpdateService {

  constructor(
    private documentService: DocumentService,
    private modifiedFieldService: ModifiedFieldService,
    private validationService: ValidationService,
    private unsavedDocumentService: UnsavedDocumentService,
    private preProcessorFieldService: PreProcessorFieldService,
    private broadcastService: BroadcastService,
    private appStore: AppStore<IApplicationState>
  ) {
  }

  private updatePrevCurrentVersion(currentVersion) {

    const _self = this;

    // in case the provided version is current one so set currentVersion of the previous one to false
    // This case for updating document from pulling documents on reconnection

    const documentId = currentVersion.documentId;
    const loadedDocs = _self.unsavedDocumentService.getAllLoadedDocs();
    const orgDocs = _self.documentService.getOrgDocuments();
    const keys = Object.keys(orgDocs);

    for (let i = 0; i < keys.length; i++) {

      const versionId = keys[i];
      const orgDoc = orgDocs[versionId];

      if (documentId === orgDoc.documentId &&
        versionId !== documentId &&
        versionId !== currentVersion.systemHeader.versionId &&
        orgDoc.systemHeader.currentVersion
      ) {

        orgDoc.systemHeader.currentVersion = false;
        _self.documentService.setOriginalDocumentOnly(orgDoc, orgDoc.systemHeader.versionId);

        loadedDocs.forEach(function(loadedDoc) {

          if (loadedDoc.systemHeader.versionId === orgDoc.systemHeader.versionId) {
            loadedDoc.systemHeader.currentVersion = false;
            _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedDoc), orgDoc.systemHeader.versionId);
          }
        });

        return;
      }
    }
  }

  async updateVersions(versions) {

    const _self = this;

    if (versions && versions.length) {

      const currentVersion = versions.filter(version => version.systemHeader.currentVersion)[0];
      if (currentVersion) {

        this.documentService.resetDisableSaveFieldToDoc(currentVersion);

        if (versions.length === 1) {
          _self.updatePrevCurrentVersion(currentVersion);
        }

        const documentId = currentVersion.documentId;

        logger.info('Updating document in memory: ' + documentId);

        const loadedDoc = cloneDeep(_self.documentService.getLoadedDocument(documentId));

        // use current version to update loaded document on form
        await _self.applyDocumentChanges(loadedDoc, currentVersion);

        // update the loaded document in store
        _self.documentService.setLoadedDocumentOnly(loadedDoc);
        // use current version to update org version in store
        _self.documentService.setOriginalDocumentOnly(currentVersion);

        logger.info('Update document on form to latest one.');

        _self.broadcastService.broadcast('document:updated', [currentVersion]);
      }

      const nonCurrentVersions = versions.filter(version => version.systemHeader.currentVersion === false);
      if (nonCurrentVersions.length) {

        // user non current version to update loaded non-current version in store
        nonCurrentVersions.forEach(async function(version) {

          const versionId = version.systemHeader.versionId;
          const loadedVersion = _self.documentService.getLoadedDocument(versionId);

          if (loadedVersion) {
            await _self.applyDocumentChanges(loadedVersion, version);

            // update loaded non-current version on form
            _self.documentService.setLoadedDocumentOnly(cloneDeep(loadedVersion), versionId);
            // use non-current version to update its version in store
            _self.documentService.setOriginalDocumentOnly(version, versionId);
          }

        });
      }

    }
  }

  async applyDocumentChanges(oldOrgDoc, latestDoc) {

    const documentId = latestDoc.documentId;
    logger.info('Applying document changes for doc: ' + documentId);

    const modifiedFields = this.modifiedFieldService.getModifiedFieldsInDoc(documentId);

    var excludedOptions = {};
    excludedOptions[SharedConstants.MAKE_CURRENT_VERSION_KEY] = true;
    const loadedDocs = this.unsavedDocumentService.getAllLoadedDocs(excludedOptions);

    for (let i = 0; i < loadedDocs.length; i++) {

      const loadedDoc = cloneDeep(loadedDocs[i]);

      if (loadedDoc.documentId === documentId) {

        const updates = await DocumentUpdater.getUpdates(oldOrgDoc, latestDoc);

        if (updates.updates && updates.updates.length) {

          if (modifiedFields && modifiedFields.length) {

            modifiedFields.forEach(function(modifiedField) {

              modifiedField = UtilString.convertToObjectPath(modifiedField, '/');

              updates.updates.forEach(function(update: any, index) {

                const path = UtilString.convertToObjectPath(update.path, '/');

                if (modifiedField === path &&

                  !isEqual(loadedDoc[modifiedField], latestDoc[modifiedField])) {

                  logger.warn('Field in pushed document: \'' +
                    modifiedField + '\' has been changed to: \'' + JSON.stringify(update.value));

                  delete updates.updates[index];

                }

              });

            });

          }

          await DocumentUpdater.applyUpdates(oldOrgDoc, updates);
        }
      }
    }
  }

  async updateFormDocument(unsavedDocuments, doc) {

    if (unsavedDocuments.length) {

      // just mutate the document if so that references to this object are not cutoff
      // also update loaded docs too
      for (let j = 0; j < unsavedDocuments.length; j++) {
        const unsavedDoc = cloneDeep(unsavedDocuments[j]);

        if (unsavedDoc.documentId === doc.documentId) {

          const updates = await DocumentUpdater.getUpdates(unsavedDoc, doc);
          await DocumentUpdater.applyUpdates(unsavedDoc, updates);
          this.appStore.dispatch(formSetDocument(unsavedDoc));

          break;
        }
      }

    }
  }

}
